import { useEffect, useRef, useState } from "react";
import { BREAKPOINTS } from "../styles/tokens";

/**
 * useViewport — single resize listener feeding three booleans.
 *
 * Drives all responsive branching in the Explorer. Use these booleans rather
 * than CSS media queries when JS layout decisions are required (e.g., switching
 * the package drawer from side-panel to bottom-sheet).
 */
export function useViewport() {
  const [w, setW] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1280
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return {
    w,
    isMobile: w < BREAKPOINTS.mobile,
    isTablet: w >= BREAKPOINTS.mobile && w < BREAKPOINTS.tablet,
    isDesktop: w >= BREAKPOINTS.tablet,
  };
}

/**
 * usePrefersReducedMotion — tracks the user's `prefers-reduced-motion` setting.
 *
 * CSS media queries can neutralize CSS animations/transitions, but they cannot
 * reach JS-driven motion (requestAnimationFrame loops, mouse-driven inline
 * transforms). Use this hook to short-circuit that motion. SSR-safe: returns
 * `false` when `window`/`matchMedia` is unavailable.
 */
export function usePrefersReducedMotion(): boolean {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = useState<boolean>(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(query);
    const onChange = () => setReduced(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

/**
 * useCountUp — animates a number from 0 to `target` using requestAnimationFrame
 * and a cubic ease-out. Used by the hero metric strip.
 *
 * Honors `prefers-reduced-motion`: when reduced motion is preferred, the value
 * snaps straight to `target` with no animation. No external dependencies; safe
 * to use anywhere counters appear.
 */
export function useCountUp(target: number, duration = 1400): number {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion) {
      setVal(target);
      return;
    }

    let raf: number;
    let start: number | undefined;

    const tick = (t: number) => {
      if (start === undefined) start = t;
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, prefersReducedMotion]);

  return val;
}

/**
 * useLockBodyScroll — locks body scroll while a modal/drawer/sheet is open.
 * Restores the previous overflow value on unmount.
 */
export function useLockBodyScroll(active: boolean) {
  useEffect(() => {
    if (!active || typeof document === "undefined") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active]);
}

/**
 * useSwipeToDismiss — touch-drag-down-to-close for bottom sheets.
 *
 * Attach `handlers` to the sheet's grab-handle / header region (not the whole
 * scrollable body, so in-content scrolling isn't hijacked) and apply
 * `translateY(dragY)` to the sheet container. Dragging past `threshold`px on
 * release invokes `onDismiss`; otherwise the sheet snaps back to 0.
 */
export function useSwipeToDismiss(onDismiss: () => void, threshold = 90) {
  const [dragY, setDragY] = useState(0);
  const startY = useRef<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (startY.current == null) return;
    const dy = e.touches[0].clientY - startY.current;
    // Only track downward drag; light resistance for a natural feel.
    setDragY(dy > 0 ? dy : 0);
  };
  const onTouchEnd = () => {
    if (dragY > threshold) onDismiss();
    setDragY(0);
    startY.current = null;
  };

  return { dragY, handlers: { onTouchStart, onTouchMove, onTouchEnd } };
}

/**
 * useEscapeKey — invokes the callback when the user presses Escape.
 * Use to dismiss drawers, sheets, and modal nav.
 */
export function useEscapeKey(onEscape: () => void, active = true) {
  useEffect(() => {
    if (!active || typeof window === "undefined") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onEscape();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onEscape, active]);
}
